import type { AnalyticsCollectInput, AnalyticsEventInput } from '@lesnoe-delo/shared';

/**
 * Собственный счётчик посещений: просмотры страниц, клики и цели уходят на
 * наш сервер, в раздел «Аналитика» админки. Работает и там, где Метрика не
 * грузится.
 *
 * Без согласия на аналитические cookie счётчик анонимен: визит живёт только в
 * sessionStorage вкладки. Постоянный id браузера (чтобы узнать вернувшегося
 * посетителя) появляется только после «Принять все».
 */

const ENDPOINT = '/api/pulse';
const SESSION_KEY = 'ld_session';
const VISITOR_KEY = 'ld_visitor';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const FLUSH_INTERVAL_MS = 5_000;
const HEARTBEAT_INTERVAL_MS = 30_000;
const MAX_QUEUE = 50;
const MAX_LABEL_LENGTH = 80;
const MAX_META_KEYS = 10;
const UNTRACKED_PREFIX = '/admin';

type Transport = (body: string) => void;
type MetaValue = string | number | boolean | null;

interface StoredSession {
  id: string;
  lastActivity: number;
}

const state = {
  isStarted: false,
  hasConsent: false,
  queue: [] as AnalyticsEventInput[],
  /** Откуда пришли на эту загрузку страницы — снимается сразу, до SPA-переходов. */
  entry: null as AnalyticsCollectInput['context'] | null,
  context: null as AnalyticsCollectInput['context'] | null,
  lastActivity: 0,
  cleanups: [] as (() => void)[],
  transport: null as Transport | null,
};

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const random = () => Math.random().toString(16).slice(2, 10).padEnd(8, '0');
  return `${random()}-${random()}-${random()}-${random()}`;
}

/** Хранилище может быть недоступно (приватный режим, запрет cookie) — тогда живём в памяти. */
function readStorage(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(storage: () => Storage, key: string, value: string): void {
  try {
    storage().setItem(key, value);
  } catch {
    // Без хранилища визит просто не переживёт перезагрузку страницы.
  }
}

let memorySession: StoredSession | null = null;

function parseSession(raw: string | null): StoredSession | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    return typeof parsed.id === 'string' && typeof parsed.lastActivity === 'number'
      ? { id: parsed.id, lastActivity: parsed.lastActivity }
      : null;
  } catch {
    return null;
  }
}

/** Визит продлевается любой активностью; после 30 минут тишины начинается новый. */
function currentSessionId(now: number, forceNew: boolean): { id: string; isNew: boolean } {
  const stored = parseSession(readStorage(() => sessionStorage, SESSION_KEY)) ?? memorySession;
  const isAlive = !forceNew && stored !== null && now - stored.lastActivity < SESSION_TIMEOUT_MS;
  const session = { id: isAlive ? stored.id : generateId(), lastActivity: now };
  memorySession = session;
  writeStorage(() => sessionStorage, SESSION_KEY, JSON.stringify(session));
  return { id: session.id, isNew: !isAlive };
}

function visitorId(): string | null {
  if (!state.hasConsent) return null;
  const existing = readStorage(() => localStorage, VISITOR_KEY);
  if (existing) return existing;
  const created = generateId();
  writeStorage(() => localStorage, VISITOR_KEY, created);
  return created;
}

/** Ссылки с секретами внутри: токен сброса пароля не должен осесть в статистике. */
const SECRET_PATH = /^\/reset\/[^/?#]+/;
const SECRET_PARAM = /^(token|code|key|password|secret|auth)$/i;

/** Путь без секретов: сегмент токена и секретные параметры заменяются заглушкой. */
export function maskPath(path: string): string {
  const [pathname = '', query] = path.split('?', 2);
  const safePathname = pathname.replace(SECRET_PATH, '/reset/***');
  if (!query) return safePathname;
  const params = new URLSearchParams(query);
  for (const key of [...params.keys()]) {
    if (SECRET_PARAM.test(key)) params.set(key, '***');
  }
  return `${safePathname}?${params.toString()}`;
}

/** Внешний реферер — без query: в нём бывают чужие идентификаторы и поисковые запросы. */
function safeReferrer(referrer: string): string | null {
  try {
    const url = new URL(referrer);
    return `${url.origin}${url.pathname}`;
  } catch {
    return null;
  }
}

/** Откуда пришёл посетитель — снимается в начале визита. */
function buildContext(isFirstInTab: boolean): AnalyticsCollectInput['context'] {
  const params = new URLSearchParams(location.search);
  return {
    // Новый визит внутри открытой вкладки — уже не переход с внешнего сайта.
    referrer: isFirstInTab && document.referrer ? safeReferrer(document.referrer) : null,
    landingPath: maskPath(`${location.pathname}${location.search}`),
    utmSource: params.get('utm_source'),
    utmMedium: params.get('utm_medium'),
    utmCampaign: params.get('utm_campaign'),
    utmTerm: params.get('utm_term'),
    utmContent: params.get('utm_content'),
    hasAdClickId: params.has('yclid') || params.has('gclid'),
    screenWidth: typeof screen === 'undefined' ? null : screen.width,
  };
}

/**
 * Пришёл по рекламе, метке или с другого сайта — это новый визит, даже если
 * вкладка открыта: иначе переход из рекламы прилипнет к прошлому прямому заходу.
 */
function hasAttribution(context: AnalyticsCollectInput['context']): boolean {
  if (context.utmSource || context.utmCampaign || context.hasAdClickId) return true;
  if (!context.referrer) return false;
  try {
    return new URL(context.referrer).host !== location.host;
  } catch {
    return false;
  }
}

function defaultTransport(body: string): void {
  const blob = new Blob([body], { type: 'text/plain' });
  if (typeof navigator !== 'undefined' && navigator.sendBeacon?.(ENDPOINT, blob)) return;
  void fetch(ENDPOINT, {
    method: 'POST',
    body,
    keepalive: true,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'text/plain' },
  }).catch(() => undefined);
}

function isTrackedPath(path: string): boolean {
  return !path.startsWith(UNTRACKED_PREFIX);
}

function currentPath(): string {
  return `${location.pathname}${location.search}`;
}

function push(event: Omit<AnalyticsEventInput, 'at'>): void {
  if (!isTrackedPath(event.path)) return;
  state.lastActivity = Date.now();
  state.queue.push({
    ...event,
    path: maskPath(event.path),
    href: event.href && event.href.startsWith('/') ? maskPath(event.href) : event.href,
    at: state.lastActivity,
  });
  if (state.queue.length >= MAX_QUEUE) flushTracker();
}

/** Отправка накопленного. Пустой пакет — «ещё здесь», чтобы считать длительность. */
export function flushTracker(isHeartbeat = false): void {
  if (state.queue.length === 0 && !isHeartbeat) return;

  const now = Date.now();
  const isFirstFlush = state.context === null;
  const entry = isFirstFlush ? (state.entry ?? buildContext(true)) : null;
  const session = currentSessionId(now, entry !== null && hasAttribution(entry));
  const context = entry ?? (session.isNew || !state.context ? buildContext(false) : state.context);
  state.context = context;

  // Визит, начатый кликом после долгой паузы, иначе остался бы без просмотра
  // и попал бы в отказы с непонятной страницей входа.
  if (session.isNew && !state.queue.some((event) => event.type === 'pageview')) {
    state.queue.unshift({
      type: 'pageview',
      name: null,
      path: maskPath(currentPath()),
      label: null,
      href: null,
      meta: null,
      at: now,
    });
  }

  const payload: AnalyticsCollectInput = {
    sessionId: session.id,
    visitorId: visitorId(),
    hasConsent: state.hasConsent,
    context,
    events: state.queue.splice(0, MAX_QUEUE),
  };
  (state.transport ?? defaultTransport)(JSON.stringify(payload));
}

function heartbeat(): void {
  const isVisible = typeof document === 'undefined' || document.visibilityState === 'visible';
  const isActive = Date.now() - state.lastActivity < SESSION_TIMEOUT_MS;
  if (isVisible && isActive && state.lastActivity > 0 && isTrackedPath(currentPath())) {
    flushTracker(true);
  }
}

function sanitizeMeta(params?: Record<string, unknown>): Record<string, MetaValue> | null {
  if (!params) return null;
  const entries = Object.entries(params)
    .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value) || value === null)
    .slice(0, MAX_META_KEYS)
    .map(([key, value]) => [key, typeof value === 'string' ? value.slice(0, 200) : value]);
  return entries.length > 0 ? (Object.fromEntries(entries) as Record<string, MetaValue>) : null;
}

export function trackPageview(path: string): void {
  push({ type: 'pageview', name: null, path, label: null, href: null, meta: null });
}

export function trackGoal(name: string, params?: Record<string, unknown>): void {
  push({
    type: 'goal',
    name,
    path: currentPath(),
    label: null,
    href: null,
    meta: sanitizeMeta(params),
  });
}

const CLICKABLE = 'a, button, [role="button"], [data-track]';

const EMAIL_PATTERN = /[^\s@]+@[^\s@]+/g;
const NUMBER_PATTERN = /\+?\d[\d\s()-]{4,}\d/g;

/** Подпись кнопки без личных данных: почта и номера в тексте заменяются заглушкой. */
function labelOf(element: Element): string | null {
  const explicit = element.getAttribute('data-track') ?? element.getAttribute('aria-label');
  const text = explicit ?? element.textContent ?? '';
  const normalized = text
    .replace(EMAIL_PATTERN, '[почта]')
    .replace(NUMBER_PATTERN, '[номер]')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_LABEL_LENGTH);
  return normalized || element.getAttribute('title');
}

/** Внутренние ссылки храним путём, внешние и tel:/mailto: — как есть. */
function hrefOf(element: Element): string | null {
  const raw = element.getAttribute('href');
  if (!raw) return null;
  try {
    const url = new URL(raw, location.href);
    if (url.origin === location.origin) return `${url.pathname}${url.search}`;
    return url.protocol.startsWith('http') ? `${url.origin}${url.pathname}` : url.href;
  } catch {
    return raw;
  }
}

export function trackClick(target: EventTarget | null): void {
  if (!(target instanceof Element)) return;
  const element = target.closest(CLICKABLE);
  if (!element || element.closest('[data-no-track]')) return;
  push({
    type: 'click',
    name: null,
    path: currentPath(),
    label: labelOf(element),
    href: hrefOf(element),
    meta: null,
  });
}

/** Разрешение на постоянный id браузера — из баннера cookie. */
export function setTrackingConsent(hasConsent: boolean): void {
  state.hasConsent = hasConsent;
}

export function startTracker(transport?: Transport): void {
  if (state.isStarted || typeof window === 'undefined') return;
  state.isStarted = true;
  state.transport = transport ?? null;
  state.entry = buildContext(true);

  const onClick = (event: Event) => trackClick(event.target);
  // При уходе со страницы отправляем сразу: sendBeacon доживает до закрытия вкладки.
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') flushTracker();
  };
  const onPageHide = () => flushTracker();
  const flushTimer = setInterval(() => flushTracker(), FLUSH_INTERVAL_MS);
  const heartbeatTimer = setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);

  document.addEventListener('click', onClick, { capture: true });
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);

  state.cleanups.push(
    () => document.removeEventListener('click', onClick, { capture: true }),
    () => document.removeEventListener('visibilitychange', onVisibility),
    () => window.removeEventListener('pagehide', onPageHide),
    () => clearInterval(flushTimer),
    () => clearInterval(heartbeatTimer),
  );
}

/** Только для тестов. */
export function resetTracker(): void {
  for (const cleanup of state.cleanups) cleanup();
  Object.assign(state, {
    isStarted: false,
    hasConsent: false,
    queue: [],
    entry: null,
    context: null,
    lastActivity: 0,
    cleanups: [],
    transport: null,
  });
  memorySession = null;
}

/** Только для тестов: тикнуть пульс без ожидания таймера. */
export { heartbeat as runHeartbeatForTests };
